import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Button, PageHeader } from '@/components/ui/primitives';
import { EngineDashboard } from '@/components/EngineDashboard';
import { InsightCard } from '@/components/InsightCard';
import { InsightText } from '@/components/InsightText';
import { Score } from '@/components/Score';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { CreateDialog } from '@/components/CreateDialog';
import { fetchSeoOverview, createSeoProject } from '@/data/sources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { formatCurrency } from '@/lib/utils';
import type { SeoProject } from '@/types';

/* /seo/projects — SEO projects (domains). Demonstrates the <EngineDashboard>
 * signature layout + evidence-first visibility score + honest source attribution
 * and the per-workspace spend cap (brief v1 §4c). Metrics the API does not
 * provide yet render as explicit "no data" — never invented numbers. */

function ProjectCard({ p }: { p: SeoProject }) {
  const spendPct =
    p.monthly_spend !== null && p.spend_cap ? Math.min(100, (p.monthly_spend / p.spend_cap) * 100) : null;
  const healthTone = p.health === 'good' ? 'success' : p.health === 'warning' ? 'warning' : 'danger';
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="font-medium text-text-primary">{p.domain}</div>
        {p.health ? (
          <Badge tone={healthTone}>{p.health}</Badge>
        ) : (
          <Badge tone="neutral">{p.status_label ?? 'active'}</Badge>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Metric label="Keywords" value={p.tracked_keywords !== null ? String(p.tracked_keywords) : '—'} />
        <Metric label="Avg. position" value={p.avg_position !== null ? p.avg_position.toFixed(1) : '—'} />
        <div>
          <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Visibility</div>
          <div className="mt-0.5">
            {p.visibility_score !== null && p.visibility_evidence ? (
              <Score value={p.visibility_score} outOf={100} evidence={p.visibility_evidence} label={`${p.domain} visibility`} />
            ) : (
              <span className="text-sm text-text-tertiary" title="Visibility is computed once rank checks have run">
                No data yet
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Spend guard — per-project spend only when the API provides it */}
      {spendPct !== null && p.monthly_spend !== null && p.spend_cap !== null && (
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-[11px] text-text-tertiary">
            <span>Monthly spend</span>
            <span className="font-mono" data-tabular>
              {formatCurrency(p.monthly_spend)} / {formatCurrency(p.spend_cap)} cap
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-inset">
            <div
              className={spendPct > 85 ? 'h-full bg-warning' : 'h-full bg-engine'}
              style={{ width: `${spendPct}%` }}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-sm font-semibold text-text-primary" data-tabular>{value}</div>
    </div>
  );
}

export function SeoProjectsPage({ title = 'SEO Projects' }: { title?: string } = {}) {
  const { activeWorkspace } = useAuth();
  const canCreate = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['seo', 'overview'],
    queryFn: fetchSeoOverview,
  });

  const handleCreate = async (values: Record<string, string>) => {
    try {
      const project = await createSeoProject({
        domain: values.domain,
        display_name: values.display_name,
      });
      setDialogOpen(false);
      setNotice(`Added ${project.domain}.`);
      await queryClient.invalidateQueries({ queryKey: ['seo', 'overview'] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        throw new Error('A project for this domain already exists.');
      }
      if (err instanceof ApiError && err.status === 400) {
        throw new Error(err.message || 'Please check the domain and display name and try again.');
      }
      throw err instanceof Error ? err : new Error('Could not add the domain. Please try again.');
    }
  };

  const addDialog = canCreate ? (
    <CreateDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title="Add domain"
      description="Track a new domain — keywords, rankings and site health, all source-attributed."
      submitLabel="Add domain"
      onSubmit={handleCreate}
      fields={[
        {
          name: 'domain',
          label: 'Domain',
          required: true,
          placeholder: 'example.com',
          hint: 'The website you want to track (without https://).',
        },
        {
          name: 'display_name',
          label: 'Display name',
          required: true,
          placeholder: 'Example Clinic',
        },
      ]}
    />
  ) : null;

  const addButton = canCreate ? (
    <Button variant="primary" onClick={() => setDialogOpen(true)}>
      ＋ Add domain
    </Button>
  ) : null;

  if (isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { projects, spend, insights } = data;
  const counted = projects.filter((p) => p.tracked_keywords !== null);
  const totalKeywords = counted.reduce((s, p) => s + (p.tracked_keywords ?? 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title={title} actions={addButton} />

      {notice && (
        <div className="rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel="Tracked keywords across all projects"
        hero={<span data-tabular>{counted.length ? totalKeywords : '—'}</span>}
        heroSub="Data from licensed APIs (DataForSEO · Google). Not a proprietary index — sources always attributed."
        insights={
          insights.length
            ? insights.map((ins) => (
                <InsightCard key={ins.title} title={ins.title} confidence={ins.confidence}>
                  <InsightText text={ins.body} />
                </InsightCard>
              ))
            : undefined
        }
      >
        {/* Workspace spend guard (GET /v1/seo/spend) — budget protection is a
            first-class, always-visible number when available. */}
        {spend && (
          <Card className="flex items-center justify-between p-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-tertiary">
                Workspace provider spend (this month)
              </div>
              <div className="mt-0.5 font-mono text-sm font-semibold text-text-primary" data-tabular>
                {formatCurrency(spend.spent)} / {formatCurrency(spend.budget)} cap
              </div>
            </div>
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-inset">
              <div
                className={
                  spend.budget > 0 && spend.spent / spend.budget > 0.85
                    ? 'h-full bg-warning'
                    : 'h-full bg-engine'
                }
                style={{
                  width: `${spend.budget > 0 ? Math.min(100, (spend.spent / spend.budget) * 100) : 0}%`,
                }}
              />
            </div>
          </Card>
        )}

        {projects.length === 0 ? (
          <EmptyState
            title="No domains tracked yet"
            teach="Add your first domain to start tracking keywords, rankings and site health — every data point arrives source-attributed, and provider spend is capped by your monthly budget."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} />
            ))}
          </div>
        )}
      </EngineDashboard>

      {addDialog}
    </div>
  );
}
